import { Slider } from "@mui/material";
import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  sliderStyles,
  MASTER_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { dashboardOdooServicesNotSyncedList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";

function OdooServicesList() {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const [pageNum, setPageNum] = useState(1);
  const [serviceListState, setServiceListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    priceRange: [0, 10000],
    filterParams: [],
  });

  // ✅ Store the Odoo API response in local state
  const [odooResponse, setOdooResponse] = useState(null);

  // --- Fetch Odoo not-synced services list ---
  useEffect(() => {
    const fetchData = async () => {
      setServiceListState((prev) => ({ ...prev, isLoading: true }));

      const filters = filterState.filterParams?.filters;
      const searchString = filterState.filterParams?.searchString || "";

      try {
        const res = await dashboardOdooServicesNotSyncedList({
          pageNum,
          searchString,
          filters:
            filters && filters !== "[]" && filters.length > 2
              ? filters
              : undefined,
        });
        setOdooResponse(res?.data || res);
      } catch (err) {
        setOdooResponse({
          code: 500,
          message: "Failed to load services list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Sync loader / error state ---
  useEffect(() => {
    if (!odooResponse) return;

    if (odooResponse.code === 200) {
      setServiceListState({
        isLoading: false,
        error: "",
        noRecords: (odooResponse?.data?.Service?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (odooResponse.code || odooResponse.message) {
      setServiceListState({
        isLoading: false,
        error:
          odooResponse.message ||
          "Failed to load services list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [odooResponse]);

  const formattedData =
    odooResponse?.data?.Service?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
          (odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  const totalRecords = odooResponse?.data?.totalRecords || 0;
  const totalFilteredRecords = odooResponse?.data?.totalFilteredRecords || 0;
  const noOfRecordsPerPage =
    odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE;

  // --- Table columns ---
  const columns = [
    {
      Header: () => <span style={{ textAlign: "center" }}>SR. NO.</span>,
      accessor: "srNo",
      className: "col-srNo",
      centerAlign: true,
      width: 60,
      minWidth: 60,
      maxWidth: 80,
    },
    {
      Header: () => <div style={{ textAlign: "left" }}>TITLE</div>,
      accessor: "title",
      className: "service-title",
      width: 180,
      minWidth: 140,
      maxWidth: 220,
      Cell: ({ value }) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </div>
      ),
    },
    {
      Header: () => <div style={{ textAlign: "left" }}>DESCRIPTION</div>,
      accessor: "description",
      className: "col-desc",
      width: 250,
      minWidth: 200,
      maxWidth: 350,
      Cell: ({ value }) => (
        <div
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            maxHeight: "60px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {value}
        </div>
      ),
    },
    {
      Header: () => (
        <div style={{ textAlign: "right", paddingRight: "20px" }}>
          CHARGES (SAR)
        </div>
      ),
      accessor: "charges",
      className: "service-charges",
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      Cell: ({ value }) => (
        <div
          style={{
            textAlign: "center",
            fontWeight: "500",
            paddingRight: "20px",
          }}
        >
          {value}
        </div>
      ),
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>STATUS</div>,
      accessor: "status",
      className: "service-status",
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: "left" }}>
          <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
            {value}
          </Badge>
        </div>
      ),
    },
  ];

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }
    if (
      filterState.priceRange &&
      (filterState.priceRange[0] !== 0 || filterState.priceRange[1] !== 10000)
    ) {
      filterParams.push({
        field: "Charges",
        condition: "btw",
        filterValues: [
          String(filterState.priceRange[0]),
          String(filterState.priceRange[1]),
        ],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : undefined;
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    if (
      !searchString &&
      (!filterState.searchStatus || filterState.searchStatus === null) &&
      filterState.priceRange[0] === 0 &&
      filterState.priceRange[1] === 10000
    ) {
      return;
    }

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };

    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newParams)
    ) {
      setServiceListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setServiceListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchName: "",
      searchStatus: null,
      priceRange: [0, 10000],
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Custom renderers for Status dropdown
  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="mb-2">
                 <div className="d-flex flex-column align-items-start">
                <CardTitle tag="h3" className="font-weight-normal">
                  Odoo Services — Not Synced
                </CardTitle>
                </div>
              </CardHeader>

              <CardBody className="services-card-body">
                {/* Filters */}
                <div className="d-flex flex-wrap align-items-center justify-content-start filter-group">
                  <Input
                    type="text"
                    maxLength={150}
                    placeholder="Search by title or description"
                    className="mr-2 mb-2 filter-input-large"
                    value={filterState.searchName}
                    onChange={(e) =>
                      setFilterState((prev) => ({
                        ...prev,
                        searchName: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={MASTER_STATUS_OPTIONS}
                    placeholder="Status"
                    value={filterState.searchStatus}
                    onChange={(selected) =>
                      setFilterState((prev) => ({
                        ...prev,
                        searchStatus: selected,
                      }))
                    }
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    components={{ Option, SingleValue }}
                    onKeyDown={handleKeyDown}
                  />

                  <div className="mx-4 slider-style">
                    <Row className="align-items-center">
                      <Col xs="6">
                        <Label className="text-muted">Service Charges</Label>
                      </Col>
                      <Col xs="6" className="text-right">
                        <Label className="text-muted">
                          SAR (
                          {filterState.priceRange[0] === 10000
                            ? `${filterState.priceRange[0]} - Above`
                            : `${filterState.priceRange[0]} - ${filterState.priceRange[1]}`}
                          )
                        </Label>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="6">
                        <Slider
                          size="small"
                          value={filterState.priceRange}
                          onChange={(e, newValue) => {
                            if (newValue[0] === 10000) {
                              newValue[1] = 100000;
                            }
                            setFilterState((prev) => ({
                              ...prev,
                              priceRange: newValue,
                            }));
                          }}
                          onKeyDown={handleKeyDown}
                          min={0}
                          max={10000}
                          step={100}
                          marks={[
                            { value: 0, label: "0" },
                            { value: 2500, label: "2.5k" },
                            { value: 5000, label: "5k" },
                            { value: 7500, label: "7.5k" },
                            { value: 10000, label: "10k" },
                          ]}
                          sx={sliderStyles}
                        />
                      </Col>
                    </Row>
                  </div>

                  <Button
                    color="default"
                    className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                    onClick={handleSearch}
                  >
                    {serviceListState.showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin search-loader" />
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" />
                        Search
                      </>
                    )}
                  </Button>

                  {filterState.filterParams?.filters || filterState.searchName !== ""  ? (
                    <Button
                      color="default"
                      className="mb-3 filter-btn btn-simple mt-2"
                      onClick={handleClear}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  ) : ""}
                </div>

                {/* Records count */}
                <div className="mt-3 mb-4 mx-1">
                  {totalRecords > 0 &&
                    (filterState.filterParams?.filters?.length > 0 ||
                    filterState.filterParams?.searchString?.trim() ? (
                      <span className="font-weight-bold">
                        Showing {totalFilteredRecords} of {totalRecords} Service
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Services: {totalRecords}
                      </span>
                    ))}
                </div>

                {/* Table */}
                {serviceListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : serviceListState.error ? (
                  <div className="loaderContainer">{serviceListState.error}</div>
                ) : serviceListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No Services found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      className="-striped -highlight table-hover"
                      data={formattedData}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      showPaginationBottom={true}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        totalFilteredRecords / noOfRecordsPerPage
                      )}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default OdooServicesList;